<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
// Section 2 — What is E-SCHOOL?
// Reference: 2. HOME.png
// Layout: white bg, 2-col (text+bullets left | video thumbnail right)

import { ref, computed, onMounted } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import { getLecturers } from '~/constants/lecturers';

const { t, locale } = useI18n();

const videoRef = ref<HTMLVideoElement | null>(null);
const bgVideoRef = ref<HTMLVideoElement | null>(null);
const containerRef = ref<HTMLDivElement | null>(null);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const isMuted = ref(true); // Default muted to allow autoplay
const isLoading = ref(true);
const bufferedPercent = ref(0);

const onWaiting = () => {
  isLoading.value = true;
};

const onPlaying = () => {
  isLoading.value = false;
};

const onSeeking = () => {
  isLoading.value = true;
};

const onSeeked = () => {
  isLoading.value = false;
  if (bgVideoRef.value && videoRef.value) {
    bgVideoRef.value.currentTime = videoRef.value.currentTime;
  }
};

const onCanPlay = () => {
  isLoading.value = false;
};

const onLoadStart = () => {
  isLoading.value = true;
};

const onLoadedData = () => {
  isLoading.value = false;
};

const onProgress = () => {
  if (!videoRef.value) return;
  if (!duration.value || duration.value === Infinity || isNaN(duration.value)) {
    duration.value = videoRef.value.duration || 0;
  }
  if (!duration.value) return;
  const buffered = videoRef.value.buffered;
  if (buffered.length > 0) {
    let maxBuffered = 0;
    for (let i = 0; i < buffered.length; i++) {
      if (buffered.start(i) <= currentTime.value && buffered.end(i) >= currentTime.value) {
        maxBuffered = buffered.end(i);
        break;
      }
    }
    if (maxBuffered === 0 && buffered.length > 0) {
      maxBuffered = buffered.end(buffered.length - 1);
    }
    bufferedPercent.value = (maxBuffered / duration.value) * 100;
  }
};

const onPlay = () => {
  isPlaying.value = true;
  if (bgVideoRef.value && videoRef.value) {
    bgVideoRef.value.currentTime = videoRef.value.currentTime;
    bgVideoRef.value.play().catch(() => {});
  }
};

const onPause = () => {
  isPlaying.value = false;
  bgVideoRef.value?.pause();
};

const togglePlay = () => {
  if (!videoRef.value) return;
  if (isPlaying.value) {
    videoRef.value.pause();
  } else {
    videoRef.value.play();
  }
};

const onTimeUpdate = () => {
  if (!videoRef.value) return;
  currentTime.value = videoRef.value.currentTime;
  if (!duration.value || duration.value === Infinity || isNaN(duration.value)) {
    duration.value = videoRef.value.duration || 0;
  }
  onProgress();
};

const onLoadedMetadata = () => {
  if (!videoRef.value) return;
  duration.value = videoRef.value.duration || 0;
  onProgress();
};

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
};

const progressPercent = computed(() => {
  if (!duration.value) return 0;
  return (currentTime.value / duration.value) * 100;
});

const seek = (event: MouseEvent) => {
  if (!videoRef.value || !duration.value) return;
  const rect = (event.currentTarget as HTMLDivElement).getBoundingClientRect();
  const clickX = event.clientX - rect.left;
  const width = rect.width;
  const seekTime = (clickX / width) * duration.value;
  videoRef.value.currentTime = seekTime;
  if (bgVideoRef.value) {
    bgVideoRef.value.currentTime = seekTime;
  }
  currentTime.value = seekTime;
};

const toggleMute = () => {
  if (!videoRef.value) return;
  isMuted.value = !isMuted.value;
  videoRef.value.muted = isMuted.value;
};

const toggleFullscreen = () => {
  if (!containerRef.value) return;
  if (document.fullscreenElement) {
    document.exitFullscreen();
  } else {
    containerRef.value.requestFullscreen();
  }
};

// Autoplay when in viewport
onMounted(() => {
  if (containerRef.value) {
    useIntersectionObserver(
      containerRef,
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        const isIntersecting = entry.isIntersecting;
        if (!videoRef.value) return;
        if (isIntersecting) {
          videoRef.value
            .play()
            .then(() => {
              isPlaying.value = true;
            })
            .catch(() => {
              // Handle autoplay restrictions gracefully
              isPlaying.value = false;
            });
        } else {
          videoRef.value.pause();
          isPlaying.value = false;
        }
      },
      { threshold: 0.2 } // Trigger when 20% of the section is visible
    );
  }
});

const formatTeacher = (text: string) => {
  const prefixes = ['Professor', 'លោកសាស្ត្រាចារ្យ', 'សាស្ត្រាចារ្យ'];
  for (const prefix of prefixes) {
    if (text.startsWith(prefix)) {
      return {
        prefix,
        rest: text.slice(prefix.length),
      };
    }
  }
  return {
    prefix: '',
    rest: text,
  };
};

const teachersList = computed(() => {
  return getLecturers(locale.value).map((lecturer) => {
    const formatted = formatTeacher(lecturer.name);
    const restSuffix =
      locale.value === 'kh' ? `${formatted.rest} ${lecturer.subject}` : `${formatted.rest} - ${lecturer.subject}`;
    return {
      prefix: formatted.prefix,
      rest: restSuffix,
    };
  });
});
</script>

<template>
  <section id="about" class="w-full bg-white">
    <div class="mx-auto max-w-[1440px] px-6 py-16 md:px-20">
      <div class="grid grid-cols-1 items-start gap-10 md:grid-cols-2 md:gap-16">
        <!-- ── Left: Heading + paragraph + bullet list ── -->
        <div class="flex flex-col">
          <h2 class="text-[26px] leading-tight font-extrabold text-gray-900 sm:text-[30px] md:text-[32px]">
            {{ t('whatIsEs.title') }}
          </h2>

          <p class="mt-4 text-[13.5px] leading-[1.8] whitespace-pre-line text-gray-700">
            {{ t('whatIsEs.desc') }}
          </p>

          <ul class="mt-5 space-y-3">
            <li
              v-for="(teacher, idx) in teachersList"
              :key="idx"
              class="flex items-start gap-2.5 text-[13.5px] text-gray-800"
            >
              <span class="mt-[2px] shrink-0 font-bold">•</span>
              <span>
                <span v-if="teacher.prefix" class="font-semibold">
                  {{ teacher.prefix }}
                </span>
                {{ teacher.rest }}
              </span>
            </li>
          </ul>
        </div>

        <!-- ── Right: Custom Video Player ── -->
        <div
          ref="containerRef"
          class="group relative h-[220px] w-full overflow-hidden rounded-2xl bg-black shadow-2xl sm:h-[300px] md:h-[340px]"
        >
          <!-- Blurred background video to avoid clipping/black borders -->
          <video
            ref="bgVideoRef"
            src="https://staging-eschool.sgp1.digitaloceanspaces.com/e-talk/202607/5ec35b6681768d64933cf6f31784099953343.mp4"
            class="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover opacity-50 blur-2xl"
            autoplay
            playsinline
            preload="auto"
            loop
            muted
          />

          <!-- Video element (default muted to allow autoplay) -->
          <video
            ref="videoRef"
            src="https://staging-eschool.sgp1.digitaloceanspaces.com/e-talk/202607/5ec35b6681768d64933cf6f31784099953343.mp4"
            class="relative z-10 h-full w-full cursor-pointer object-contain"
            autoplay
            playsinline
            preload="auto"
            loop
            :muted="isMuted"
            @click="togglePlay"
            @play="onPlay"
            @pause="onPause"
            @timeupdate="onTimeUpdate"
            @loadedmetadata="onLoadedMetadata"
            @durationchange="onLoadedMetadata"
            @ended="isPlaying = false"
            @waiting="onWaiting"
            @playing="onPlaying"
            @seeking="onSeeking"
            @seeked="onSeeked"
            @canplay="onCanPlay"
            @loadstart="onLoadStart"
            @loadeddata="onLoadedData"
            @progress="onProgress"
          />

          <!-- Loading Spinner overlay -->
          <div
            v-show="isLoading"
            class="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-black/45"
          >
            <div class="h-10 w-10 animate-spin rounded-full border-4 border-white/20 border-t-white" />
          </div>

          <!-- Play button overlay (visible when paused and not loading) -->
          <div
            v-show="!isPlaying && !isLoading"
            class="absolute inset-0 z-10 flex cursor-pointer items-center justify-center bg-black/25"
            @click="togglePlay"
          >
            <button
              class="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-xl transition hover:scale-110 hover:bg-white"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6 translate-x-0.5 text-gray-800"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
          </div>

          <!-- Controls bar -->
          <div
            class="absolute right-0 bottom-0 left-0 z-20 flex items-center gap-3 bg-black/75 px-4 py-[10px] transition-opacity duration-300"
            :class="isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'"
          >
            <!-- Play/Pause Button -->
            <button class="shrink-0 text-white transition hover:text-white/80" @click="togglePlay">
              <svg
                v-if="isPlaying"
                xmlns="http://www.w3.org/2000/svg"
                class="h-[14px] w-[14px]"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="h-[14px] w-[14px]"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>

            <!-- Current time -->
            <span class="shrink-0 font-mono text-[11px] text-white/80 select-none">
              {{ formatTime(currentTime) }}
            </span>

            <!-- Progress bar -->
            <div
              class="relative h-[4px] flex-1 cursor-pointer rounded-full bg-white/20 transition-all hover:h-[6px]"
              @click="seek"
            >
              <!-- Buffer progress -->
              <div
                class="absolute top-0 left-0 h-full rounded-full bg-white/40"
                :style="{ width: bufferedPercent + '%' }"
              />
              <!-- Active progress -->
              <div
                class="absolute top-0 left-0 h-full rounded-full bg-emerald-500"
                :style="{ width: progressPercent + '%' }"
              />
            </div>

            <!-- Duration -->
            <span class="shrink-0 font-mono text-[11px] text-white/80 select-none">
              {{ formatTime(duration) }}
            </span>

            <!-- Volume / Mute Toggle -->
            <button class="shrink-0 text-white/70 transition hover:text-white" @click="toggleMute">
              <svg
                v-if="isMuted"
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z"
                />
                <path stroke-linecap="round" stroke-linejoin="round" d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M15.536 8.464a5 5 0 010 7.072M12 6v12M9 9H6a1 1 0 00-1 1v4a1 1 0 001 1h3l4 4V5L9 9z"
                />
              </svg>
            </button>

            <!-- Fullscreen -->
            <button class="shrink-0 text-white/70 transition hover:text-white" @click="toggleFullscreen">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-3.5 w-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/* When the container is in fullscreen mode */
:fullscreen {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background-color: #000000 !important;
}

:-webkit-full-screen {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background-color: #000000 !important;
}

:fullscreen video {
  width: auto !important;
  height: auto !important;
  max-width: 80vw !important;
  max-height: 80vh !important;
  object-fit: contain !important;
}

:-webkit-full-screen video {
  width: auto !important;
  height: auto !important;
  max-width: 80vw !important;
  max-height: 80vh !important;
  object-fit: contain !important;
}
</style>
